import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface CommentDraftRange {
  from: number;
  to: number;
}

/** Class the decoration adds; styled in `styles.css` beside `.cotera-comment`. */
const DRAFT_CLASS = 'cotera-comment-draft';

const commentDraftKey = new PluginKey<CommentDraftRange | null>(
  'commentDraftHighlight'
);

const draftRangesEqual = (
  a: CommentDraftRange | null,
  b: CommentDraftRange | null
): boolean => {
  if (a === null || b === null) {
    return a === b;
  }
  return a.from === b.from && a.to === b.to;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    commentDraftHighlight: {
      /**
       * Marks the range a comment is currently being written about, or clears it
       * with `null`.
       */
      setCommentDraftRange: (range: CommentDraftRange | null) => ReturnType;
    };
  }
}

/**
 * Keeps the words you are commenting on visible while you write the comment.
 *
 * Selecting text and opening the composer moves focus out of the editor, and a browser
 * stops painting a selection the moment its element is blurred — so at the one point
 * where it matters most, the thing being annotated looked exactly like the text around
 * it. This paints the range itself, in the colour the finished annotation will have,
 * so the composer is visibly attached to something.
 *
 * Driven by a command rather than read off the selection, because by the time the
 * composer has focus the selection is no longer the interesting thing: the range has
 * to survive the caret going somewhere else entirely. It is mapped through every
 * transaction so that edits elsewhere in the document do not leave it pointing at the
 * wrong words.
 */
export const CommentDraftHighlight = Extension.create({
  name: 'commentDraftHighlight',

  addCommands() {
    return {
      setCommentDraftRange:
        (range) =>
        ({ tr, state, dispatch }) => {
          // No-op when the draft range is unchanged (e.g. clearing an
          // already-clear range on mount/cleanup). Dispatching here would run
          // an empty transaction through `applyTransaction`, firing every
          // plugin's `appendTransaction` — including id assignment — over the
          // current document for no reason. On a document whose stored
          // `editor_state` is schema-invalid, that normalization pass throws
          // (`contentMatchAt on a node with invalid content`) and takes down
          // the whole editor, so the cheapest correct fix is to never emit the
          // pointless transaction in the first place.
          //
          // `editor.commands.*` dispatches the shared transaction regardless of
          // this function's return value, so suppressing the dispatch requires
          // the `preventDispatch` meta rather than just returning `false`.
          const current = commentDraftKey.getState(state) ?? null;
          if (draftRangesEqual(current, range)) {
            // `preventDispatch` is Tiptap core's own transaction meta, checked in
            // CommandManager before `view.dispatch`. Because the transaction is never
            // dispatched, no plugin or appendTransaction ever observes it — there is
            // nothing elsewhere that needs to honor this flag.
            tr.setMeta('preventDispatch', true);
            return false;
          }
          if (dispatch) {
            dispatch(tr.setMeta(commentDraftKey, range));
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<CommentDraftRange | null>({
        key: commentDraftKey,
        state: {
          init: () => null,
          apply(tr, value) {
            const meta = tr.getMeta(commentDraftKey) as
              | CommentDraftRange
              | null
              | undefined;
            if (meta !== undefined) {
              return meta;
            }
            if (value === null || !tr.docChanged) {
              return value;
            }
            const from = tr.mapping.map(value.from);
            const to = tr.mapping.map(value.to);
            // Deleting the annotated words while the composer is open leaves nothing
            // to point at, and a zero-width decoration renders as a stray sliver.
            return from < to ? { from, to } : null;
          },
        },
        props: {
          decorations: (state) => {
            const range = commentDraftKey.getState(state);
            if (range == null) {
              return null;
            }
            return DecorationSet.create(state.doc, [
              Decoration.inline(range.from, range.to, { class: DRAFT_CLASS }),
            ]);
          },
        },
      }),
    ];
  },
});
